/* eslint-disable react/prop-types -- Props are typed with TypeScript interfaces */
import { useEffect, useMemo, useState } from 'react';

import { type Session, SessionListType, type User } from '@/types';
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableEmpty,
  TableHead,
  TableLoading,
  TableRow,
  Typography,
} from '@/ui';

import SessionList from '@/components/Sessions/List';
import {
  useGetCoachAttendanceReportQuery,
  useGetCoachSessionCountQuery,
  useGetCoachSessionsDashboardQuery,
  useGetCoachSummaryQuery,
} from '@/store/api/sessionApi';

interface CoachSummaryProps {
  coach: User;
}

interface SessionCountsCardProps {
  isLoading: boolean;
  sessionCounts:
    | {
        pending: number;
        done: number;
        active: number;
        rescheduled: number;
        cancelled: number;
      }
    | undefined;
}

interface MemberStageCardProps {
  isLoading: boolean;
  summary:
    | {
        memberStageCounts: Array<[string, number]>;
        totalPastMembers: number;
        chemistryCallMembers: string[];
        noFutureSessions: string[];
      }
    | undefined;
}

interface QuickOverviewCardProps {
  isLoading: boolean;
  dash:
    | {
        upcoming: Record<string, Session>;
        noUpcoming: string[];
        noSessions: string[];
      }
    | undefined;
  summary:
    | {
        memberStageCounts: Array<[string, number]>;
        totalPastMembers: number;
        chemistryCallMembers: string[];
        noFutureSessions: string[];
      }
    | undefined;
  summaryLoading: boolean;
}

const ATTENDANCE_PAGE_SIZE = 10;

const formatRate = (rate: number) => `${(rate * 100).toFixed(0)}%`;

const SessionCountsCard: React.FC<SessionCountsCardProps> = ({
  isLoading,
  sessionCounts,
}) => (
  <div className="rounded-lg bg-white p-4 shadow">
    <Typography className="mb-3" variant="h6">
      Session Counts
    </Typography>
    {isLoading && <div className="text-gray-500">Loading...</div>}
    {!isLoading && sessionCounts && (
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Active:</span>
          <span className="font-semibold">{sessionCounts.active}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Pending:</span>
          <span className="font-semibold">{sessionCounts.pending}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Done:</span>
          <span className="font-semibold">{sessionCounts.done}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Rescheduled:</span>
          <span className="font-semibold">{sessionCounts.rescheduled}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Cancelled:</span>
          <span className="font-semibold">{sessionCounts.cancelled}</span>
        </div>
      </div>
    )}
    {!isLoading && !sessionCounts && (
      <div className="text-gray-500">No session data available</div>
    )}
  </div>
);

const MemberStageCard: React.FC<MemberStageCardProps> = ({
  isLoading,
  summary,
}) => (
  <div className="rounded-lg bg-white p-4 shadow">
    <Typography className="mb-3" variant="h6">
      Member Stages
    </Typography>
    {isLoading && <div className="text-gray-500">Loading...</div>}
    {!isLoading && summary && (
      <div className="space-y-2">
        {summary.memberStageCounts.map(([stage, count]) => (
          <div key={stage} className="flex justify-between">
            <span className="capitalize text-gray-600">
              {stage.replace('_', ' ')}:
            </span>
            <span className="font-semibold">{count}</span>
          </div>
        ))}
        <hr className="my-2" />
        <div className="flex justify-between">
          <span className="text-gray-600">Total Past Members:</span>
          <span className="font-semibold">{summary.totalPastMembers}</span>
        </div>
      </div>
    )}
    {!isLoading && !summary && (
      <div className="text-gray-500">No summary data available</div>
    )}
  </div>
);

const QuickOverviewCard: React.FC<QuickOverviewCardProps> = ({
  isLoading,
  dash,
  summary,
  summaryLoading,
}) => (
  <div className="rounded-lg bg-white p-4 shadow">
    <Typography className="mb-3" variant="h6">
      Quick Overview
    </Typography>
    {summaryLoading || isLoading ? (
      <div className="text-gray-500">Loading...</div>
    ) : (
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Upcoming Sessions:</span>
          <span className="font-semibold">
            {Object.keys(dash?.upcoming || {}).length}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">No Upcoming:</span>
          <span className="font-semibold">{dash?.noUpcoming.length || 0}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">No Sessions:</span>
          <span className="font-semibold">{dash?.noSessions.length || 0}</span>
        </div>
        {summary && (
          <div className="flex justify-between">
            <span className="text-gray-600">Chemistry Calls:</span>
            <span className="font-semibold">
              {summary.chemistryCallMembers.length}
            </span>
          </div>
        )}
      </div>
    )}
  </div>
);

const CoachSummary: React.FC<CoachSummaryProps> = ({ coach }) => {
  const [attendancePage, setAttendancePage] = useState(1);

  useEffect(() => {
    setAttendancePage(1);
  }, [coach.id]);

  const { data: dash, isLoading } = useGetCoachSessionsDashboardQuery(
    { coachId: coach.id },
    { skip: !coach.id },
  );

  const { data: sessionCounts, isLoading: sessionCountsLoading } =
    useGetCoachSessionCountQuery({ coachId: coach.id }, { skip: !coach.id });

  const { data: summary, isLoading: summaryLoading } = useGetCoachSummaryQuery(
    { coachId: coach.id },
    { skip: !coach.id },
  );
  const {
    data: attendanceReport,
    isError: attendanceError,
    isLoading: attendanceLoading,
  } = useGetCoachAttendanceReportQuery(
    {
      coachId: coach.id,
      limit: ATTENDANCE_PAGE_SIZE,
      page: attendancePage,
    },
    { skip: !coach.id },
  );

  const displayedAttendancePage = attendanceReport?.current ?? attendancePage;
  let attendanceMemberCountLabel = `${attendanceReport?.total ?? 0} members`;
  if (attendanceLoading) {
    attendanceMemberCountLabel = 'Loading...';
  } else if (attendanceError) {
    attendanceMemberCountLabel = 'Unable to load';
  }

  const nextSession = useMemo(() => {
    if (!dash?.upcoming) return {} as Record<string, Session[]>;
    const sessions = Object.values(dash.upcoming) as Session[];
    if (sessions.length === 0) return {} as Record<string, Session[]>;
    return { 'Next session': [sessions[0]] } as Record<string, Session[]>;
  }, [dash]);

  const upcomingSessions = useMemo(() => {
    if (!dash?.upcoming) return {} as Record<string, Session[]>;
    const sessions = Object.values(dash.upcoming) as Session[];
    if (sessions.length <= 1) return {} as Record<string, Session[]>;
    return { Upcoming: sessions.slice(1) } as Record<string, Session[]>;
  }, [dash]);

  return (
    <div className="flex-1 space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <SessionCountsCard
          isLoading={sessionCountsLoading}
          sessionCounts={sessionCounts}
        />
        <MemberStageCard isLoading={summaryLoading} summary={summary} />
        <QuickOverviewCard
          dash={dash}
          isLoading={isLoading}
          summary={summary}
          summaryLoading={summaryLoading}
        />
      </div>

      <div>
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <Typography variant="h6">Attendance (Last 30 Days)</Typography>
            <Typography className="text-cement-400" variant="body2">
              Per-member attendance counts from validated session data.
            </Typography>
          </div>
          <Typography className="text-cement-400" variant="body2">
            {attendanceMemberCountLabel}
          </Typography>
        </div>

        <TableContainer className="rounded-lg bg-white shadow">
          <Table aria-label="coach attendance report">
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell align="right">Sessions</TableCell>
                <TableCell align="right">Verified</TableCell>
                <TableCell align="right">Unverified</TableCell>
                <TableCell align="right">Mismatch</TableCell>
                <TableCell align="right">No data</TableCell>
                <TableCell align="right">Verified rate</TableCell>
              </TableRow>
            </TableHead>

            {attendanceLoading && <TableLoading columns={7} rows={4} />}
            {!attendanceLoading && attendanceError && (
              <TableEmpty
                columns={7}
                message="Attendance report unavailable. Please try again."
              />
            )}
            {!attendanceLoading &&
              !attendanceError &&
              !!attendanceReport?.items.length && (
                <TableBody>
                  {attendanceReport.items.map((row) => (
                    <TableRow key={row.memberId}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{row.memberName}</span>
                          <span className="text-sm text-cement-400">
                            {row.memberEmail}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell align="right">{row.totalSessions}</TableCell>
                      <TableCell align="right">
                        {row.verifiedSessions}
                      </TableCell>
                      <TableCell align="right">
                        {row.unverifiedSessions}
                      </TableCell>
                      <TableCell align="right">
                        {row.mismatchSessions}
                      </TableCell>
                      <TableCell align="right">{row.noDataSessions}</TableCell>
                      <TableCell align="right">
                        {formatRate(row.verifiedRate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            {!attendanceLoading &&
              !attendanceError &&
              !attendanceReport?.items.length && (
                <TableEmpty
                  columns={7}
                  message="No attendance records found for this reporting window."
                />
              )}
          </Table>
        </TableContainer>

        {attendanceReport && attendanceReport.pages > 1 && (
          <div className="mt-4 flex justify-end">
            <Pagination
              count={attendanceReport.pages}
              page={displayedAttendancePage}
              onChange={(_, value) => setAttendancePage(value)}
            />
          </div>
        )}
      </div>

      {/* Sessions */}
      <div>
        <Typography variant="h6">Sessions</Typography>
        <div className="mt-4">
          <SessionList
            sessions={nextSession as Record<string, Session[]>}
            type={SessionListType.Next}
            hideIfEmpty
          />
          <SessionList
            loading={isLoading}
            sessions={upcomingSessions as Record<string, Session[]>}
            type={SessionListType.Upcoming}
          />
        </div>
      </div>
    </div>
  );
};

export default CoachSummary;

/* eslint-enable react/prop-types */
